import React from 'react';

import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

import { MODE_OPTIONS, type AgentMode } from '../AgentModeSelector';

export interface ModeToggleProps {
  mode: AgentMode;
  onChange: (mode: AgentMode) => void;
  disabled?: boolean;
}

/**
 * Compact agent-mode switch (Ask / Ask Before Edits / Auto Edit). Reuses the
 * legacy `MODE_OPTIONS` so labels and the parked `agent_auto` stay in sync.
 */
const ModeToggle: React.FC<ModeToggleProps> = ({
  mode,
  onChange,
  disabled,
}) => (
  <ToggleGroup
    value={[mode]}
    onValueChange={(next: unknown[]) => {
      const value = next[0];
      if (typeof value === 'string') onChange(value as AgentMode);
    }}
    variant="outline"
    size="sm"
    spacing={0}
    disabled={disabled}
    aria-label="Agent mode"
    data-testid="ai-mode-toggle"
    className="agent-mode-toggle"
  >
    {MODE_OPTIONS.map((opt) => (
      <Tooltip key={opt.value}>
        {/* The toggle owns the button (pressed/disabled state); the tooltip
            trigger is layered on via `render` so both contexts apply. */}
        <ToggleGroupItem
          value={opt.value}
          disabled={opt.disabled}
          aria-label={opt.label}
          className="tw:px-2.5 tw:text-xs"
          render={<TooltipTrigger />}
        >
          {opt.shortLabel}
        </ToggleGroupItem>
        <TooltipContent className="dark">{opt.label}</TooltipContent>
      </Tooltip>
    ))}
  </ToggleGroup>
);

export default ModeToggle;
